import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} ReviewMarqueeRefs
 * @property {HTMLElement} track - The window the lane travels behind.
 * @property {HTMLElement} lane - The row of cards that moves.
 */

/**
 * A row of cards that drifts sideways without end.
 *
 * The movement is one CSS animation on the lane, not a scroll position nudged
 * from a timer: the compositor owns it, so it stays smooth while the main thread
 * is busy and it costs nothing to keep running.
 *
 * Looping seamlessly needs the lane to be longer than the window and to repeat
 * on a known interval. So the original cards are cloned until the lane is wide
 * enough to cover the window twice over, and the animation travels exactly one
 * original set — at which point the first clone is standing where the first card
 * began and the restart is invisible. Measuring that distance rather than using
 * a percentage is what lets any number of cards of any width work unchanged.
 *
 * The clones are `aria-hidden` and taken out of the tab order: they are the same
 * reviews again, and a screen reader or a keyboard should meet each one once.
 *
 * Without this script the lane is simply a scrollable row of reviews, which is
 * also what it becomes for anyone who has asked for reduced motion.
 *
 * @extends {Component<ReviewMarqueeRefs>}
 */
class ReviewMarqueeComponent extends Component {
  requiredRefs = ['track', 'lane'];

  /** @type {Element[]} */
  #originals = [];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) return;

    this.#originals = [...this.refs.lane.children];
    if (!this.#originals.length) return;

    this.#resizeObserver = new ResizeObserver(this.#requestMeasure);
    this.#resizeObserver.observe(this.refs.track);

    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #requestMeasure = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#measure);
  };

  /**
   * Rebuilds the lane for the current width and publishes the loop distance.
   *
   * Re-run on resize because the number of copies needed depends on how wide the
   * window is, and a phone rotated to landscape can need more of them.
   */
  #measure = () => {
    const { track, lane } = this.refs;

    // Back to just the originals, so the measurement is of one set and the
    // clone count is decided fresh rather than compounding.
    for (const child of [...lane.children]) {
      if (!this.#originals.includes(child)) child.remove();
    }

    const gap = parseFloat(getComputedStyle(lane).columnGap) || 0;
    const distance = lane.scrollWidth + gap;

    if (distance <= gap) return;

    // Two windows' worth means there is always a full screen of cards arriving
    // behind the ones leaving, whichever way the lane is travelling.
    const needed = Math.max(1, Math.ceil((track.clientWidth * 2) / distance));

    for (let copy = 0; copy < needed; copy++) {
      for (const original of this.#originals) {
        const clone = /** @type {HTMLElement} */ (original.cloneNode(true));

        clone.setAttribute('aria-hidden', 'true');
        for (const focusable of clone.querySelectorAll('a, button, input, select, textarea')) {
          focusable.setAttribute('tabindex', '-1');
        }

        lane.append(clone);
      }
    }

    // The section sets a pace in pixels per second; the duration follows from
    // how far one loop actually is, so a row of thirty reviews drifts at the
    // same speed as a row of three rather than racing to finish in the time.
    const pace = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-pace')) || 50;

    lane.style.setProperty('--marquee-distance', `${distance}px`);
    lane.style.setProperty('--marquee-duration', `${distance / pace}s`);
    this.toggleAttribute('data-marquee-ready', true);
  };
}

if (!customElements.get('review-marquee')) {
  customElements.define('review-marquee', ReviewMarqueeComponent);
}
